<template>
    <div v-if="optionsList" :class="calculatedClass" v-click-outside="closeDropdown">
        <label :for="'input-'+name">{{ calculatedLabel + (isMandatory ? ' *' : '') }}</label>
        <div class="select">
            <input
                type="text"
                :id="'input-'+name"
                v-model="searchText"
                :placeholder="$t('wizard.dropdown_default')"
                :class="errorClass"
                autocomplete="off"
                @focus="openDropdown"
                @input="onInput"/>
            <button v-if="searchText" class="clear-btn" type="button" @click="clearSelection">&#x2715;</button>
            <ul v-if="isOpen && filteredCount > 0" class="dropdown">
                <li
                    v-for="(label, value) in filteredOptions"
                    :key="value"
                    @mousedown.prevent="selectOption(value)">
                    {{ value }} <span class="payer-hint">{{ label }}</span>
                </li>
            </ul>
            <p v-if="isOpen && searchText && filteredCount === 0" class="no-results">
                {{ $t('wizard.dropdown_default') }}
            </p>
        </div>
        <p v-if="goingToShowWarningMessage">&#9888; {{ warningMessage }}</p>
    </div>
    <span v-if="isSingleInLine" :class="calculatedClass"/>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export default {
  directives: {
    clickOutside: {
      mounted (el, binding) {
        el._clickOutside = (event) => {
          if (!el.contains(event.target)) binding.value()
        }
        document.addEventListener('click', el._clickOutside)
      },
      unmounted (el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },
  props: {
    name: { type: String, required: true },
    label: { type: String, default: '' },
    isMandatory: { type: Boolean, default: false },
    optionsList: { type: Object, default () { return {} } },
    size: { type: String, default: 'half' },
    warningMessage: { type: String, default: '' },
    validationToShowWarningMessage: { type: Boolean, default: false },
    isSingleInLine: { type: Boolean, default: false }
  },
  data () {
    return {
      searchText: '',
      isOpen: false
    }
  },
  computed: {
    calculatedClass () {
      return { 'full-size': this.size === 'full', 'half-size': this.size === 'half' }
    },
    errorClass () {
      return { error: this.formErrors.includes('input-' + this.name) && this.isMandatory }
    },
    calculatedLabel () {
      return this.label || this.labelFromName
    },
    form () {
      return this.$parent.$parent.form
    },
    formErrors () {
      return this.$store.state.formErrors
    },
    labelFromName () {
      const nameAsSnakeCase = this.name.replace(/([A-Z])/g, ' $1').replaceAll(' ', '_').toLowerCase()
      return this.$t('wizard.form_field_' + nameAsSnakeCase)
    },
    goingToShowWarningMessage () {
      return Boolean(this.warningMessage && this.validationToShowWarningMessage)
    },
    filteredOptions () {
      const term = this.searchText.toLowerCase()
      const result = {}
      Object.entries(this.optionsList).forEach(([value, label]) => {
        if (!term || value.toLowerCase().includes(term) || String(label).toLowerCase().includes(term)) {
          result[value] = label
        }
      })
      return result
    },
    filteredCount () {
      return Object.keys(this.filteredOptions).length
    }
  },
  watch: {
    'form' (newForm) {
      if (newForm && !newForm[this.name]) {
        this.searchText = ''
      }
    }
  },
  methods: {
    openDropdown () {
      this.isOpen = true
    },
    closeDropdown () {
      this.isOpen = false
      // If text doesn't match a valid option, clear both display and form value
      const match = Object.keys(this.optionsList).find(
        v => v.toLowerCase() === this.searchText.toLowerCase()
      )
      if (!match) {
        this.searchText = ''
        this.form[this.name] = ''
      }
    },
    onInput () {
      this.isOpen = true
      this.form[this.name] = ''
    },
    selectOption (value) {
      this.form[this.name] = value
      this.searchText = value
      this.isOpen = false
    },
    clearSelection () {
      this.searchText = ''
      this.form[this.name] = ''
      this.isOpen = false
    }
  }
}
</script>

<style lang="scss" scoped>
div {
    margin-bottom: 12px;
    position: relative;
    label {
        display: block;
        margin-bottom: 5px;
        font-weight: bolder;
    }
    .select {
        margin-bottom: 7px;
        position: relative;
        input {
            width: 100%;
            min-height: 50px;
            line-height: 48px;
            padding: 0 60px 0 15px;
            outline: none;
            background-color: #EEEEEE;
            border: 1px solid #cccccc;
            border-radius: 4px;
            box-shadow: 1px 1px 7px -1px #b6b6b6;
            font-size: 1.1rem;
            font-family: inherit;
            box-sizing: border-box;
            &.error {
                border: 2px solid red;
            }
        }
        &::after {
            content: '\f002';
            font: normal normal normal 16px/1 FontAwesome;
            color: #404040;
            right: 15px;
            top: 17px;
            position: absolute;
            pointer-events: none;
        }
        .clear-btn {
            position: absolute;
            right: 38px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            font-size: 0.85rem;
            color: #888;
            padding: 0 2px;
            line-height: 1;
            &:hover { color: #333; }
        }
        .dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 100;
            background: #EEEEEE;
            border: 1px solid #cccccc;
            border-top: none;
            border-radius: 0 0 4px 4px;
            box-shadow: 1px 4px 7px -1px #b6b6b6;
            max-height: 220px;
            overflow-y: auto;
            list-style: none;
            margin: 0;
            padding: 0;
            li {
                padding: 10px 15px;
                font-size: 1.1rem;
                font-family: inherit;
                cursor: pointer;
                color: #333;
                .payer-hint {
                    font-size: 0.85rem;
                    color: #888;
                    margin-left: 6px;
                }
                &:hover {
                    background-color: #cccccc;
                }
            }
        }
        .no-results {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 100;
            background: #EEEEEE;
            border: 1px solid #cccccc;
            border-top: none;
            border-radius: 0 0 4px 4px;
            padding: 10px 15px;
            font-size: 1.1rem;
            color: #999;
            margin: 0;
        }
    }
    p {
        font-size: 13px;
        margin: 0;
    }
}

.full-size { width: 100%; }
.half-size { width: 48%; }

@media (max-width: 700px) {
    .full-size, .half-size { width: 100%; }
}

@media (prefers-color-scheme: dark) {
  .dark-theme {
    div .select input { box-shadow: none; }
  }
}
</style>
